import React, { useEffect, FormEvent } from 'react';
import { UnknownAction } from 'redux';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';

import {
  DynamicButton,
  PasswordField,
  DynamicButtonStatus,
  colors,
  Icon,
} from '../../../design-system';
import {
  AUTH_BAR_MAIN_TITLE,
  AUTH_BAR_SIGNIN_TITLE,
  CTA_CONTENT_SIGNIN,
  FORGOT_EMAIL_TEXT,
  MSG_CONNECT_WITH_NEW_CREDENTIALS,
  MSG_ERROR,
  MSG_MODIFY,
  MSG_PASSWORD_UPDATED,
} from '../locale';
import { Forms, FieldCallback, FormSignInPassword } from '../../form/types';
import withForm from '../../form';
import { WithFormActions } from '../../form/hocs/withForm';
import { ParamsLogin } from '../types';
import { validate, isCtaActive } from '../../form/utils';
import useInteractionLock from '../../common/hooks/useInteractionLock';
import { checkEmail, errorLogin, setHasForgotPassword } from '../actions';
import { changeTitleNew } from '../../common/actions';
import { Alert } from '../../../design-system/alert';
import { theme } from '../../../design-system/systemprovider';

type Props = {
  login: (params: ParamsLogin) => void;
  email: string;
  form: FormSignInPassword;
  errLogin: string;
  comeFromResetPassword?: boolean;
} & WithFormActions;

const StyledSignInContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: ${theme.space.m};
  margin: ${theme.space.m};
  .success-password-changed,
  .error-login,
  .login-user-email {
    font-size: 1.2rem;
    color: ${colors.MAIN_GREY};
    margin: 0;
  }
  .login-user-email {
    font-size: 1.4rem;
  }
  .sidebar__return-button {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: none;
    padding: 0;
    cursor: pointer;
    position: absolute;
    top: 4px;
    left: 4px;
  }
  .email-and-modify-button-block {
    display: flex;
    flex-wrap: wrap;
    row-gap: ${theme.space.s};
    column-gap: ${theme.space.m};
    margin-bottom: 8px;
  }
  .sign-in-form-block {
    display: grid;
    grid-gap: ${theme.space.m};
  }
  .sigin-in-hidden-user-input {
    display: none;
  }

  .signin__btn-forgot-passowrd-wrapper {
    width: fit-content;
    margin: auto;
  }

  .signin__btn-submit-password {
    text-transform: uppercase;
  }
`;

const SignIn = ({
  login,
  email,
  errLogin,
  comeFromResetPassword = false,
  form,
  setFormValues,
  setFormValidation,
}: Props) => {
  useInteractionLock(form.ctaState === DynamicButtonStatus.Loading);
  const dispatch = useDispatch();
  const validateField = ({ key, value }: FieldCallback): string =>
    validate({ value: String(value), key });

  const handleChange = ({ key, value }: FieldCallback) => {
    if (form.validation[key]) {
      setFormValidation({
        form: Forms.signInPassword,
        values: { [key]: validateField({ key, value }) },
      });
    }

    setFormValues({ form: Forms.signInPassword, values: { [key]: value } });
  };

  const validateForm = (): boolean => {
    const { values } = form;
    const newErrMsgs = form.validation;
    const errMsgs = Object.keys(values).map((key) => {
      const errMsg = validateField({ key, value: values[key] });
      newErrMsgs[key] = errMsg;
      setFormValidation({ form: Forms.signInPassword, values: { [key]: errMsg } });
      return errMsg;
    });
    return errMsgs.every((errMsg) => !errMsg);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { password } = form.values;
    if (validateForm()) {
      login({ email, password });
    }
  };

  const onModifyEmailButton = () => {
    dispatch(changeTitleNew(AUTH_BAR_MAIN_TITLE));
    dispatch(checkEmail(null) as unknown as UnknownAction);
    dispatch(errorLogin(''));
  };

  const onForgotenPasswordButtonClick = () => {
    dispatch(changeTitleNew(AUTH_BAR_MAIN_TITLE));
    dispatch(setHasForgotPassword(true));
  };

  useEffect(() => {
    handleChange({ key: 'email', value: email });
  }, [email]);

  useEffect(() => {
    dispatch(changeTitleNew(AUTH_BAR_SIGNIN_TITLE));
    return () => {
      handleChange({ key: 'password', value: '' });
      setFormValidation({ form: Forms.signInPassword, values: { password: '' } });
    };
  }, []);

  return (
    <StyledSignInContainer>
      {comeFromResetPassword && (
        <Alert type="success" title={MSG_PASSWORD_UPDATED}>
          <p id="success-password-changed" className="success-password-changed">
            {MSG_CONNECT_WITH_NEW_CREDENTIALS}
          </p>
        </Alert>
      )}
      {errLogin && (
        <Alert type="error" title={MSG_ERROR}>
          <p id="error-login" className="error-login">
            {errLogin}
          </p>
        </Alert>
      )}
      <div className="email-and-modify-button-block">
        <button
          className="sidebar__return-button"
          data-testid="signin-sidebar-return-button"
          aria-label={`Bouton pour revenir à l'étape précédente`}
          type="button"
          onClick={onModifyEmailButton}
        >
          <Icon name="chevronLeft" size={20} />
        </button>
        <p className="login-user-email">{email}</p>
        <button
          id="signin__btn-modify-mail"
          className="anchor-animated"
          data-testid="signin__btn-modify-mail"
          aria-label={`Bouton pour revenir à l'étape précédente et modifier votre email`}
          type="button"
          onClick={onModifyEmailButton}
        >
          {MSG_MODIFY}
        </button>
      </div>
      <form className="sign-in-form-block" onSubmit={handleSubmit}>
        <input
          className="sigin-in-hidden-user-input"
          type="text"
          name="email"
          value={email}
          autoComplete="username"
          readOnly
        />
        <PasswordField
          onChange={(value) => handleChange({ key: 'password', value })}
          errMsg={form.validation.password}
          value={form.values.password}
          id="signin-password"
          autoComplete="current-password"
          autoFocus
        />
        <DynamicButton
          id="btn-submit-signin"
          className="signin__btn-submit-password"
          type="submit"
          data-testid="sign-in-connect-button"
          data={CTA_CONTENT_SIGNIN}
          feedback={form.ctaState}
          disabled={!isCtaActive(form)}
        />
      </form>
      <div className="signin__btn-forgot-passowrd-wrapper">
        <button
          id="signin__btn-forgot-password"
          className="anchor-animated"
          data-testid="signin__btn-forgot-password"
          aria-label={`Bouton pour réinitialiser votre mot de passe`}
          type="button"
          onClick={onForgotenPasswordButtonClick}
        >
          {FORGOT_EMAIL_TEXT}
        </button>
      </div>
    </StyledSignInContainer>
  );
};

export const SignInWithForm = withForm(SignIn, Forms.signInPassword);
